// Control Center (design_handoff_control_center §2–§4): Seitenkopf mit „Speichern“, Fehlerbalken, Raster-Spalten
// und Demo-Modus-Widget. Die Handler kommen unverändert aus SuperAdminDashboard.
import type { CSSProperties, ReactNode } from 'react';
import { CheckCircle, CircleNotch, Eye, FloppyDisk, WarningCircle, X } from '@phosphor-icons/react';

/** Spalten im 12er-Raster aus der echten Fensterbreite: ≥ 1180 8/4, 760–1179 voll/halb, darunter einspaltig. */
export function rasterSpalten(breite: number): { gross: string; klein: string } {
  if (breite >= 1180) return { gross: 'span 8', klein: 'span 4' };
  return { gross: '1 / -1', klein: breite >= 760 ? 'span 6' : '1 / -1' };
}

/** Glas-Widget (Radius 28) mit Kopf: Icon-Kachel, Titel, Untertitel, rechts optional ein Chip. */
export function CcWidget({ titel, untertitel, icon, rechts, spalte, children, style }: {
  titel: string; untertitel?: ReactNode; icon: ReactNode; rechts?: ReactNode; spalte: string; children: ReactNode; style?: CSSProperties;
}) {
  return (
    <section aria-label={titel} className="immo-glas flex min-w-0 flex-col" style={{ gridColumn: spalte, borderRadius: 28, padding: '20px 22px 22px', gap: 16, ...style }}>
      <div className="flex flex-wrap items-center" style={{ gap: 12 }}>
        {icon}
        <div className="flex min-w-0 flex-1 flex-col">
          <h2 className="m-0" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>{titel}</h2>
          {untertitel && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{untertitel}</span>}
        </div>
        {rechts}
      </div>
      {children}
    </section>
  );
}

/** Icon-Kachel 38 px im Widget-Kopf. */
export function KopfIcon({ children, flaeche, farbe }: { children: ReactNode; flaeche: string; farbe: string }) {
  return (
    <span className="flex shrink-0 items-center justify-center" style={{ width: 38, height: 38, borderRadius: 12, background: flaeche, color: farbe, boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.35)' }}>
      {children}
    </span>
  );
}

export function SeitenKopf({ schmal, laedt, gesperrt, erfolg, onSpeichern }: { schmal: boolean; laedt: boolean; gesperrt: boolean; erfolg: boolean; onSpeichern: () => void }) {
  return (
    <header className="flex flex-wrap items-end justify-between" style={{ gap: 14 }}>
      <div className="flex min-w-0 flex-col" style={{ gap: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)' }}>System</span>
        <h1 className="m-0" style={{ fontSize: schmal ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.02 }}>Control Center</h1>
        <span className="flex items-center" style={{ gap: 7, fontSize: 14, color: 'var(--ink2)' }}>
          <span aria-hidden className="animate-pulse" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)', boxShadow: '0 0 0 3px var(--ok-soft)' }} />
          Live System Status
        </span>
      </div>
      <button type="button" onClick={onSpeichern} disabled={gesperrt} data-tip="Speichern – Portal Sektionen für alle Nutzer übernehmen"
        className="inline-flex items-center"
        style={{ height: 44, padding: '0 20px', gap: 8, borderRadius: 22, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
          cursor: gesperrt ? 'not-allowed' : 'pointer', opacity: gesperrt ? 0.5 : 1, transition: 'background .3s',
          ...(erfolg ? { background: 'var(--ok)', color: '#fff', boxShadow: 'none' } : { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }) }}>
        {laedt ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : erfolg ? <CheckCircle size={16} weight="bold" /> : <FloppyDisk size={16} weight="bold" />}
        {erfolg ? 'Gespeichert' : 'Speichern'}
      </button>
    </header>
  );
}

/** Bisher nie angezeigter error-State als Hinweisbalken (README §2, Freigabe 18.10.2026). */
export function FehlerBalken({ text, onSchliessen, mobil = false }: { text: string; onSchliessen: () => void; mobil?: boolean }) {
  return (
    <div role="alert" className="flex items-center" style={{ gap: 12, padding: '12px 16px', borderRadius: 20, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13.5, fontWeight: 700 }}>
      <WarningCircle size={20} weight="fill" className="shrink-0" />
      <span className="min-w-0 flex-1" style={{ overflowWrap: 'anywhere' }}>{text}</span>
      <button type="button" onClick={onSchliessen} aria-label="Fehlermeldung ausblenden" data-tip="Schließen – Fehlermeldung ausblenden"
        className="flex shrink-0 items-center justify-center" style={{ width: mobil ? 44 : 30, height: mobil ? 44 : 30, border: 'none', borderRadius: '50%', cursor: 'pointer', background: 'transparent', color: 'inherit' }}>
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}

const ORANGE = 'linear-gradient(160deg, color-mix(in srgb, var(--warn) 70%, #fff), var(--warn))';

export function DemoWidget({ aktiv, laedt, onUmschalten }: { aktiv: boolean; laedt: boolean; onUmschalten: () => void }) {
  return (
    <section aria-label="Demo-Modus" className="immo-glas flex flex-wrap items-center"
      style={{ gridColumn: '1 / -1', borderRadius: 28, padding: '20px 22px', gap: 18, transition: 'background .4s, box-shadow .4s',
        ...(aktiv ? { background: 'linear-gradient(160deg, var(--warn-soft), var(--warn-soft)), var(--glass)', boxShadow: 'inset 0 0 0 1.5px var(--warn), 0 20px 40px -18px var(--shade)' } : {}) }}>
      <span className="flex shrink-0 items-center justify-center"
        style={{ width: 52, height: 52, borderRadius: 16, transition: 'all .3s',
          ...(aktiv ? { background: ORANGE, color: '#fff', boxShadow: '0 12px 24px -10px color-mix(in srgb, var(--warn) 60%, transparent)' } : { background: 'var(--fill2)', color: 'var(--acc)' }) }}>
        <Eye size={26} weight="fill" />
      </span>
      <div className="flex min-w-0 flex-col" style={{ flex: '1 1 260px', gap: 4 }}>
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <h2 className="m-0" style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em' }}>Demo-Modus</h2>
          {aktiv && (
            <span className="inline-flex animate-pulse items-center" style={{ height: 22, padding: '0 10px', gap: 6, borderRadius: 11, fontSize: 11.5, fontWeight: 800, background: 'var(--warn)', color: '#fff' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />Aktiv
            </span>
          )}
        </div>
        <span style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--ink2)' }}>
          {aktiv ? 'Alle Module zeigen Beispieldaten — keine echten Daten sichtbar' : 'Zeigt realistische Beispieldaten für Kundenpräsentationen'}
        </span>
      </div>
      <button type="button" onClick={onUmschalten} disabled={laedt}
        data-tip={aktiv ? 'Deaktivieren – Wieder echte Daten anzeigen. Die Seite lädt neu.' : 'Aktivieren – Alle Module zeigen Beispieldaten. Die Seite lädt neu.'}
        className="inline-flex shrink-0 items-center"
        style={{ height: 44, padding: '0 20px', gap: 8, borderRadius: 22, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
          cursor: laedt ? 'wait' : 'pointer', opacity: laedt ? 0.5 : 1,
          ...(aktiv
            ? { background: ORANGE, color: 'color-mix(in srgb, var(--warn) 22%, #000)', boxShadow: '0 14px 28px -12px color-mix(in srgb, var(--warn) 60%, transparent)' }
            : { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }) }}>
        {laedt ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Eye size={16} weight="bold" />}
        {aktiv ? 'Deaktivieren' : 'Aktivieren'}
      </button>
    </section>
  );
}
